/**
 * Custom element for managing extra product selections in deo pdp drawer interface.
 * Handles product variant selection, quantity management, and cart operations.
 */
if (!customElements.get('drawer-extra-product')) {
  customElements.define(
    'drawer-extra-product',
    class DrawerExtraProduct extends HTMLElement {
      /**
       * Creates an instance of DrawerExtraProduct.
       * Initializes the selectedItems Map to track variant selections.
       */
      constructor() {
        super();
        /** @type {Map<string, number>} Map of variant_id to quantity */
        this.selectedItems = new Map();

        /** @type {Object} Accumulated cart responses */
        this.initialCartResponse = {};
      }

      /**
       * Called when the element is connected to the DOM.
       * Initializes all event listeners and DOM references.
       */
      connectedCallback() {
        this.initAddButtons();
        this.initItemClickListeners();
        this.initAddToBagButton();

        this.productForm = document.querySelector('product-form[deo-pdp-extra-product]');
        const mainProductForm = this.productForm.querySelector('form');
        
        this.variantInput = mainProductForm.querySelector('[name="items[0]id"]');
        if (this.variantInput) {
          this.variantInput.disabled = false;
        }
        
        // Listen for drawer open events (only attach once)
        if (!this.drawerOpenListenerAttached) {
          this.drawerOpenHandler = () => {
            this.errorMessageWrapper = ''
            this.updateSelectedVariantQuantity();
            this.isInitialCall = true;
            this.addToCart();
            this.initializeFooter();
            this.ensureFooterVisible();
          };
          document.addEventListener('drawerOrModalOpen.drawer-extra-product', this.drawerOpenHandler);
          this.drawerOpenListenerAttached = true;
        }
      }

      /**
       * Updates the selected variant quantity based on the current selling plan selection.
       * Handles one-time purchase vs subscription logic.
       */
      updateSelectedVariantQuantity() {
        this.selectedItems.clear();
        // Reset all add buttons
        this.resetAllButtons();
        this.sellingPlanId;
        const sellingPlanPicker = document.querySelector('.product-main selling-plan-picker');
        const sectionId = document.querySelector('variant-radios')?.dataset.section;

        if (sellingPlanPicker) {
          const variantSwitcher = document.querySelector(`variant-radios[data-section="${sectionId}"]`);
          if (sellingPlanPicker.elements.oneTimeRadio && sellingPlanPicker.elements.oneTimeRadio.checked) {
            this.sellingPlanId = null;
            const oneTimeProductJson = JSON.parse(document.querySelector('[data-one-time-product-json]').textContent);
          
            let oneTimeProductSameVariant;
          
            if (variantSwitcher && variantSwitcher.currentVariant) {
              this.originalVariantId = variantSwitcher.currentVariant.id
              // Try to find a matching variant by title
              oneTimeProductSameVariant = oneTimeProductJson.find(variant => 
                variant.sku === variantSwitcher.currentVariant.sku
              );
            }
          
            // If no matching variant found or variantSwitcher is null, use the first variant
            if (!oneTimeProductSameVariant && oneTimeProductJson.length > 0) {
              oneTimeProductSameVariant = oneTimeProductJson[0];
            }
          
            if (oneTimeProductSameVariant) {
              this.variantInput.value = oneTimeProductSameVariant.id;
            }
          } else {
            this.originalVariantId = variantSwitcher.currentVariant.id
            // Try to get from frequency select dropdown first
            const frequencySelect = sellingPlanPicker.querySelector('[data-selling-plan-frequency]');
            if (frequencySelect && frequencySelect.dataset.esValue) {
              this.sellingPlanId = frequencySelect.dataset.esValue;
            } else {
              // Fallback to hidden input
              const hiddenInput = sellingPlanPicker.querySelector('.selected-selling-plan-id');
              if (hiddenInput && !hiddenInput.disabled && hiddenInput.value) {
                this.sellingPlanId = hiddenInput.value;
              }
            }
            this.variantInput.value = this.originalVariantId;
          }
        }
      }

      /**
       * Initializes event listeners for item clicks.
       */
      initItemClickListeners() {
        const items = this.querySelectorAll('.item');
        items.forEach(item => {
          item.addEventListener('click', (e) => this.handleItemClick(e));
        });
      }

      /**
       * Handles click events on item divs.
       */
      handleItemClick(event) {
        const item = event.currentTarget;
        
        this.querySelectorAll('.item').forEach(i => i.classList.remove('active'));
        item.classList.add('active');

        this.updateFooterData(item);
      }

      /**
       * Updates footer data based on selected variant
       */
      updateFooterData(item) {
        const variantData = {
          id: item.dataset.variantId,
          price: item.dataset.variantPrice,
          title: item.querySelector('input[name="variant_title"]').value,
          description: item.querySelector('input[name="variant_description"]').value,
          ingredients: item.querySelector('input[name="variant_ingredients"]').value
        };

        // Update title
        const titleElement = this.querySelector('.drawer-footer .product-details .title');
        if (titleElement) {
          titleElement.textContent = variantData.title;
        }

        // Update description
        const descriptionElement = this.querySelector('.product-short-description p');
        if (descriptionElement) {
          descriptionElement.textContent = variantData.description || '';
        }

        // Update ingredients
        const ingredientsElement = this.querySelector('.drawer-footer .product-details [data-variant-all-ingredients]');
        if (ingredientsElement) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = variantData.ingredients;
          ingredientsElement.innerHTML = tempDiv.innerHTML || '';
        }

        // Update price
        const priceElement = this.querySelector('.drawer-footer .price-item--regular');
        if (priceElement) {
          const formattedPrice = parseInt(variantData.price);
          const currency = document.querySelector('.currency-symbol').textContent;
          priceElement.textContent = `${currency}${variantData.price}`;
        }

        // Update badges from active item
        const badgesContainer = this.querySelector('.drawer-footer .badges');
        const activeItem = this.querySelector('.item.active');
        
        if (badgesContainer && activeItem) {
          const activeItemBadge = activeItem.querySelector('.item-badge');
          const badgeSpan = badgesContainer.querySelector('span');
          
          if (activeItemBadge && badgeSpan) {
            // Check if active item badge has limited-edition class
            const isLimitedEdition = activeItemBadge.classList.contains('limited-edition');
            
            if (isLimitedEdition) {
              badgesContainer.style.display = 'none';
            } else {
              badgesContainer.style.display = 'block';
              const badgeContent = activeItemBadge.innerHTML;
              badgeSpan.innerHTML = badgeContent;
            }
          }
        }
      }

      /**
       * Initializes event listeners for all add buttons in the drawer.
       */
      initAddButtons() {
        const addButtons = this.querySelectorAll('.add-btn');
        addButtons.forEach(button => {
          button.addEventListener('click', (e) => this.handleAddClick(e));
        });
      }

      /**
       * Initializes the main "Add to Bag" button event listener.
       */
      initAddToBagButton() {
        this.addToBagBtn = this.querySelector('.add-to-bag-btn');
        if (this.addToBagBtn) {
          this.addToBagBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToCart();
          });
        }
      }

      /**
       * Handles click events on individual product add buttons.
       * Increments quantity and updates UI accordingly.
       * 
       * @param {Event} event - The click event
       */
      handleAddClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const item = button.closest('.item');
        const variantId = item.dataset.variantId;
        
        // Get current quantity
        const currentQty = this.selectedItems.get(variantId) || 0;
        const newQty = currentQty + 1;
        
        // Update quantity
        this.selectedItems.set(variantId, newQty);
        
        // Update UI
        this.updateButtonUI(button, newQty);
        
        // Update button text based on selections
        this.updateAddToBagButton();
      }

      /**
       * Updates the button UI to show quantity controls or quantity display.
       * 
       * @param {HTMLElement} button - The button element to update
       * @param {number} quantity - The current quantity
       */
      updateButtonUI(button, quantity) {
        const addBtn = button;
        const plusAdd = addBtn.querySelector('.plus-add');
        const inheritBox = addBtn.querySelector('.inherit-box');
        const qtyDisplay = inheritBox.querySelector('.qty-display');
        
        if (quantity === 1) {
          // First click - show quantity controls, hide text
          plusAdd.style.display = 'none';
          inheritBox.style.display = 'flex';
          qtyDisplay.textContent = quantity;
          
          // Add event listeners if not already added
          const minusBtn = inheritBox.querySelector('.minus');
          const plusBtn = inheritBox.querySelector('.plus');
          
          if (!minusBtn.hasAttribute('data-listener-added')) {
            minusBtn.addEventListener('click', (e) => this.handleQuantityChange(e, -1));
            minusBtn.setAttribute('data-listener-added', 'true');
          }
          
          if (!plusBtn.hasAttribute('data-listener-added')) {
            plusBtn.addEventListener('click', (e) => this.handleQuantityChange(e, 1));
            plusBtn.setAttribute('data-listener-added', 'true');
          }
        } else if (quantity > 1) {
          // Update quantity display
          qtyDisplay.textContent = quantity;
        } else {
          // Reset to add button
          plusAdd.style.display = 'inline';
          inheritBox.style.display = 'none';
        }
      }

      /**
       * Handles quantity changes via plus/minus buttons.
       * 
       * @param {Event} event - The click event
       * @param {number} change - The quantity change (+1 or -1)
       */
      handleQuantityChange(event, change) {
        event.preventDefault();
        event.stopPropagation();
        const button = event.target.closest('.add-qty');
        const item = button.closest('.item');
        const variantId = item.dataset.variantId;
        
        const currentQty = this.selectedItems.get(variantId) || 0;
        const newQty = Math.max(0, currentQty + change);
        
        if (newQty === 0) {
          // Reset to original "+ Add" button using show/hide
          const addBtn = button.querySelector('.add-btn');
          const plusAdd = addBtn.querySelector('.plus-add');
          const inheritBox = addBtn.querySelector('.inherit-box');
          
          plusAdd.style.display = 'inline';
          inheritBox.style.display = 'none';
          
          this.selectedItems.delete(variantId);
        } else {
          this.selectedItems.set(variantId, newQty);
          this.updateButtonUI(button.querySelector('.add-btn'), newQty);
        }
        
        // Update button text based on selections
        this.updateAddToBagButton();
      }

      /**
       * Gets the price for a specific variant ID from the drawer items
       * @param {string} variantId - The variant ID to get price for
       * @returns {number} - The price as decimal number
       */
      getVariantPrice(variantId) {
        const item = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (item && item.dataset.variantPrice) {
          return parseFloat(item.dataset.variantPrice.replace(',', '.'));
        }
        return 0;
      }

      /**
       * Updates the main "Add to Bag" button text based on current selections.
       * Shows price when items are selected, or "continue to checkout" when none selected.
       */
      updateAddToBagButton() {
        const checkoutBtn = this.addToBagBtn?.querySelector('.btn-title--checkout');
        const addToBagBtnWithPrice = this.addToBagBtn?.querySelector('.btn-title--add-to-bag-with-price');
        
        if (!checkoutBtn || !addToBagBtnWithPrice) return;
        
        const hasSelections = Array.from(this.selectedItems.entries()).some(([_, qty]) => qty > 0);
        
        if (hasSelections) {
          // Calculate total price from selected variants
          let totalPrice = 0;
          Array.from(this.selectedItems.entries()).forEach(([variantId, qty]) => {
            totalPrice += this.getVariantPrice(variantId) * qty;
          });

          // Show add to bag button with price
          checkoutBtn.style.display = 'none';
          checkoutBtn.classList.remove('active');
          addToBagBtnWithPrice.style.display = 'inline';
          addToBagBtnWithPrice.classList.add('active');
          addToBagBtnWithPrice.querySelector('.price-amount').textContent = totalPrice.toFixed(2).replace('.', ',').replace(',00', '');
        } else {
          // Show checkout button
          checkoutBtn.style.display = 'inline';
          checkoutBtn.classList.add('active');
          addToBagBtnWithPrice.style.display = 'none';
          addToBagBtnWithPrice.classList.remove('active');
        }
      }

      /**
       * Adds selected items to the cart.
       * Handles loading states and form submission logic.
       * 
       * @async
       */
      async addToCart() {
        // Prevent multiple simultaneous calls
        if (this.isAddingToCart) return;
        this.isAddingToCart = true;
        const items = [];

        if (this.isInitialCall) {
          // Add main product (get from current product form)
          const mainVariantId = this.variantInput.value;
          if (mainVariantId) {
            const mainItem = {
              id: parseInt(mainVariantId),
              quantity: 1
            };

            // Add selling plan if subscription is selected
            if (this.sellingPlanId) {
              mainItem.selling_plan = parseInt(this.sellingPlanId);
            }

            items.push(mainItem);

            if (!this.sellingPlanId) {
              const refillQuantitySelect = document.querySelector('[data-refill-quantity]');
              if (refillQuantitySelect) {
                const refillQty = parseInt(refillQuantitySelect.dataset.esValue || refillQuantitySelect.value)

                if (refillQty && refillQty > 1) {
                  const refillVariantInput = this.productForm.querySelector('[data-refill-variant-id] input[name="refill-variant-id"]');
                  const refillVariantId = refillVariantInput?.value;

                  if (refillVariantId) {
                    items.push({
                      id: parseInt(refillVariantId),
                      quantity: refillQty - 1
                    });
                  }
                }
              }
            }
          }
        } else {
          // Start loading state
          this.addToBagBtn.classList.add('loading');
          this.addToBagBtn.disabled = true;
          this.addToBagBtn.setAttribute('aria-disabled', true);
          this.addToBagBtn.querySelector('.loading-overlay')?.classList.remove('hidden');

          // Add selected variants
          Array.from(this.selectedItems.entries())
          .filter(([_, qty]) => qty > 0)
          .forEach(([variantId, qty]) => {
            items.push({
              id: parseInt(variantId),
              quantity: qty
            });
          });

          if (items.length === 0) {
            const json = this.initialCartResponse;
            const event = new CustomEvent('ajaxProduct:added', {
              detail: {
                json
              }
            });
            document.dispatchEvent(event);

            this.initialCartResponse = {};
            this.isAddingToCart = false;

            return;
          }
        }

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: JSON.stringify({ items })
        }

        try {
          const response = await fetch(routes.cart_add_url + '.js', config)
          const cartResponse = await response.json();

          if (response.status !== 200) throw new Error(cartResponse.description);

          this.initialCartResponse = { ...this.initialCartResponse, ...cartResponse };

          if (!this.isInitialCall) {
            const json = this.initialCartResponse;
            const event = new CustomEvent('ajaxProduct:added', {
              detail: {
                json
              }
            });
            document.dispatchEvent(event);
          }
        } catch (error) {
          Woolman.ModalsAndDrawers.closeModalOrDrawer('drawer-extra-product');
          this.handleErrorMessage(error);
          console.error(error);
        } finally {
          if (!this.isInitialCall) {
            // End loading state
            this.resetAllButtons();

            const activeBtn = this.addToBagBtn.querySelector('.btn-title.active');
            if (activeBtn && window.variantStrings?.added) {
              const originalHTML = activeBtn.innerHTML;
              if (!this.errorMessageWrapper) {
                activeBtn.innerHTML = window.variantStrings.added;

                setTimeout(() => {
                  activeBtn.innerHTML = originalHTML;
                }, 2000);
              }
            }

            this.initialCartResponse = {};
          }
          this.isInitialCall = false;
          this.isAddingToCart = false;
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper = this.productForm.querySelector('.product-form__error-message-wrapper')

        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;

          setTimeout(() => {
            this.errorMessageWrapper.toggleAttribute('hidden', true);
            this.errorMessageWrapper = '';
          }, 3000);
        }
      }

      /**
       * Resets all add buttons to initial state
       */
      resetAllButtons() {
        const plusAddElements = this.querySelectorAll('.plus-add');
        const inheritBoxElements = this.querySelectorAll('.inherit-box');

        plusAddElements.forEach(plusAdd => plusAdd.style.display = 'inline');
        inheritBoxElements.forEach(inheritBox => inheritBox.style.display = 'none');

        this.addToBagBtn.classList.remove('loading');
        this.addToBagBtn.disabled = false;
        this.addToBagBtn.removeAttribute('aria-disabled');
        this.addToBagBtn.querySelector('.loading-overlay')?.classList.add('hidden');
      }

      /**
       * Ensures the drawer footer is always visible
       */
      ensureFooterVisible() {
        const footer = this.querySelector('drawer-extra-product .drawer-footer');
        if (footer) {
          footer.style.removeProperty('display');
        }
      }

      /**
       * Initializes footer with first item data when drawer opens
       */
      initializeFooter() {
        const sectionId = document.querySelector('variant-radios')?.dataset.section;
        const variantSwitcher = document.querySelector(`variant-radios[data-section="${sectionId}"]`);
        let selectedVariantTitle = null;

        // Get the selected variant from main PDP
        if (variantSwitcher && variantSwitcher.currentVariant) {
          selectedVariantTitle = variantSwitcher.currentVariant.title;
        }

        // Update the scent included message
        const scentMessage = this.querySelector('[data-scent-included-message]');
        if (scentMessage && selectedVariantTitle) {
          // Split by ' / ' and get the last element (scent name)
          const titleParts = selectedVariantTitle.split(' / ');
          const scentName = titleParts[titleParts.length - 1];
          
          // Check if this scent exists in drawer items
          const items = this.querySelectorAll('.item');
          let scentExists = false;
          
          for (const item of items) {
            const variantTitle = item.querySelector('input[name="variant_title"]')?.value;
            if (variantTitle && variantTitle.toLowerCase() === scentName.toLowerCase()) {
              scentExists = true;
              break;
            }
          }
          
          // Only show message if scent exists in drawer
          if (scentExists) {
            const template = scentMessage.dataset.messageTemplate;
            const message = template.replace('{{ scent_name }}', `<strong>${scentName.toLowerCase()}</strong>`);
            scentMessage.innerHTML = message;
            scentMessage.style.display = 'block';
          } else {
            scentMessage.style.display = 'none';
          }
        }

        const firstItem = this.querySelector('.item');
        if (firstItem) {
          // Remove all active classes
          this.querySelectorAll('.item').forEach(i => i.classList.remove('active'));
          // Set first item as active
          firstItem.classList.add('active');
          // Update footer with first item data
          this.updateFooterData(firstItem);
        }
        this.updateAddToBagButton();
      }
    }
  );
}